import { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { LOOK_SENS, TOUCH_LOOK_SENS } from '../../core/config';
import {
  attachInput,
  consumeLook,
  detachInput,
  getInput,
  requestPointerLock,
} from '../../core/input';
import { useGameStore } from '../../core/store';
import {
  TUNNEL_PANELS,
  TUNNEL_START_Z,
  EXIT_FP_STOP_Z,
  EXIT_CHECKPOINT_Z,
  EXIT_CHECKPOINT_RADIUS,
} from '../../data/tunnel';
import { skillBoards } from '../../data/skillBoards';
import { playTunnelFootstep, playSkillUnlock, setTunnelBedIntensity } from '../audio';
import { useTunnelExitStore } from './tunnelExitStore';
import {
  resetExitHandoffLock,
  scheduleExitHandoff,
} from './TunnelExitCinematic';

const WALK_SPEED = 2.55;
/** Career unlock when player reaches this distance before board Z */
const CAREER_UNLOCK_SLACK = 0.65;
/** Match stamina starts low and fills as you walk the tunnel */
const STAMINA_START = 0.1;

/** Tunnel bed only: silent at entrance → full at mouth (no crowd / unlock / synth). */
function tunnelBedForZ(z: number) {
  return THREE.MathUtils.smoothstep(z, TUNNEL_START_Z, EXIT_FP_STOP_Z);
}

/**
 * First-person tunnel walk. Crossing the cyan ring at the mouth
 * blackouts, then VO (mobile and desktop).
 */
export function TunnelWalker() {
  const { camera, gl } = useThree();
  const pos = useRef(new THREE.Vector3(0, 1.55, TUNNEL_START_Z));
  const yaw = useRef(Math.PI);
  const pitch = useRef(-0.04);
  const bob = useRef(0);
  const footAcc = useRef(0);
  const exiting = useRef(false);
  const prevCamZ = useRef(TUNNEL_START_Z);
  const _fwd = useRef(new THREE.Vector3());
  const _right = useRef(new THREE.Vector3());
  const _wish = useRef(new THREE.Vector3());

  const setRunnerPose = useGameStore((s) => s.setRunnerPose);
  const setTunnelStamina = useGameStore((s) => s.setTunnelStamina);
  const unlockSkill = useGameStore((s) => s.unlockSkill);
  const triggerUnlockSlow = useGameStore((s) => s.triggerUnlockSlow);
  const setActiveTunnelPanel = useGameStore((s) => s.setActiveTunnelPanel);
  const resetExit = useTunnelExitStore((s) => s.reset);
  const maxZ = useRef(TUNNEL_START_Z);

  useEffect(() => {
    resetExit();
    resetExitHandoffLock();
    maxZ.current = TUNNEL_START_Z;
    exiting.current = false;
    prevCamZ.current = TUNNEL_START_Z;
    setTunnelStamina(STAMINA_START);
    setTunnelBedIntensity(0, 0.2);
    attachInput();
    const canvas = gl.domElement;
    const onClick = () => requestPointerLock(canvas);
    canvas.addEventListener('click', onClick);
    if (camera instanceof THREE.PerspectiveCamera) {
      camera.fov = 54;
      camera.near = 0.05;
      camera.far = 320;
      camera.updateProjectionMatrix();
    }
    camera.position.copy(pos.current);
    return () => {
      canvas.removeEventListener('click', onClick);
      detachInput();
    };
  }, [camera, gl, resetExit, setTunnelStamina]);

  useFrame((_, dt) => {
    const beat = useTunnelExitStore.getState().beat;
    const unlockSlowUntil = useGameStore.getState().unlockSlowUntil;
    const slow = performance.now() < unlockSlowUntil ? 0.35 : 1;
    const dtSlow = dt * slow;

    // Skip leftover jersey-back cinematic (HMR) — black + VO immediately
    if (beat === 'exit') {
      exiting.current = true;
      scheduleExitHandoff();
      return;
    }
    if (exiting.current || beat === 'fade') return;

    // ——— Normal first-person walk ———
    const input = getInput();
    const look = consumeLook();
    const sens =
      'ontouchstart' in window || navigator.maxTouchPoints > 0
        ? TOUCH_LOOK_SENS
        : LOOK_SENS;
    yaw.current -= look.x * sens * slow;
    pitch.current = THREE.MathUtils.clamp(
      pitch.current - look.y * sens * slow,
      -0.55,
      0.45,
    );
    yaw.current = THREE.MathUtils.clamp(
      yaw.current,
      Math.PI - 0.9,
      Math.PI + 0.9,
    );

    _fwd.current.set(-Math.sin(yaw.current), 0, -Math.cos(yaw.current));
    _right.current.set(Math.cos(yaw.current), 0, -Math.sin(yaw.current));
    _wish.current
      .set(0, 0, 0)
      .addScaledVector(_fwd.current, input.forward)
      .addScaledVector(_right.current, input.right);
    if (_wish.current.lengthSq() > 1) _wish.current.normalize();

    const nearExit = pos.current.z > EXIT_FP_STOP_Z - 4;
    setTunnelBedIntensity(tunnelBedForZ(pos.current.z));
    const speedMul = nearExit ? 0.92 : 1;
    const speed = WALK_SPEED * (input.sprint ? 1.25 : 1) * speedMul * slow;

    pos.current.x = THREE.MathUtils.clamp(
      pos.current.x + _wish.current.x * speed * dt,
      -1.45,
      1.45,
    );
    pos.current.z = THREE.MathUtils.clamp(
      pos.current.z + _wish.current.z * speed * dt,
      TUNNEL_START_Z - 0.2,
      EXIT_FP_STOP_Z + 0.02,
    );

    const moving = _wish.current.lengthSq() > 0.01;
    bob.current += dtSlow * (moving ? 7.2 : 1.2);
    const breathe = Math.sin(performance.now() * 0.0014) * 0.012;
    const bobY = moving ? Math.sin(bob.current) * 0.028 : breathe;
    const handX = Math.sin(bob.current * 0.5) * (moving ? 0.012 : 0.004);

    camera.up.set(0, 1, 0);
    camera.position.set(
      pos.current.x + handX,
      pos.current.y + bobY,
      pos.current.z,
    );
    camera.rotation.order = 'YXZ';
    camera.rotation.y = yaw.current;
    camera.rotation.x = pitch.current;
    camera.rotation.z = 0;

    if (camera instanceof THREE.PerspectiveCamera) {
      const targetFov = nearExit ? 46 : 54;
      camera.fov = THREE.MathUtils.lerp(camera.fov, targetFov, dt * 2);
      camera.updateProjectionMatrix();
    }

    setRunnerPose({
      z: pos.current.z,
      x: pos.current.x,
      yaw: yaw.current,
    });

    for (const board of skillBoards) {
      if (pos.current.z < board.z - CAREER_UNLOCK_SLACK) break;
      if (useGameStore.getState().unlockedSkills.includes(board.id)) continue;
      unlockSkill(board.id);
      void playSkillUnlock();
      triggerUnlockSlow(420);
      break;
    }

    if (moving) {
      if (pos.current.z > maxZ.current) maxZ.current = pos.current.z;
      footAcc.current += dtSlow;
      if (footAcc.current > (nearExit ? 0.48 : 0.46)) {
        footAcc.current = 0;
        playTunnelFootstep(input.sprint);
      }
    }
    const span = Math.max(1, EXIT_FP_STOP_Z - TUNNEL_START_Z);
    const distFill = THREE.MathUtils.clamp(
      (maxZ.current - TUNNEL_START_Z) / span,
      0,
      1,
    );
    const targetStamina = STAMINA_START + distFill * (1 - STAMINA_START);
    setTunnelStamina(
      THREE.MathUtils.damp(
        useGameStore.getState().tunnelStamina,
        targetStamina,
        5.5,
        dt,
      ),
    );

    let nearest: string | null = null;
    let best = 2.8;
    for (const p of TUNNEL_PANELS) {
      const d = Math.abs(pos.current.z - p.z);
      if (d < best) {
        best = d;
        nearest = p.id;
      }
    }
    setActiveTunnelPanel(nearest);

    const camZ = camera.position.z;
    const camX = camera.position.x;
    const ringGate = EXIT_CHECKPOINT_RADIUS * 0.9;
    const crossedRing =
      prevCamZ.current < EXIT_CHECKPOINT_Z &&
      camZ >= EXIT_CHECKPOINT_Z &&
      Math.abs(camX) <= ringGate;
    prevCamZ.current = camZ;
    if (crossedRing) {
      exiting.current = true;
      setTunnelStamina(1);
      document.exitPointerLock?.();
      scheduleExitHandoff();
    }
  });

  return null;
}
